import { useState, useEffect } from 'react';
import { MapPin, Phone, Clock } from 'lucide-react';
import { publicApi } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useReveal } from '@/hooks/useScrollAnimation';


export default function ContactSection() {
  const { lang, t } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [headerRef, headerVisible] = useReveal(0.1);
  const [mapRef, mapVisible] = useReveal(0.05);
  const [infoRef, infoVisible] = useReveal(0.1);

  useEffect(() => {
  publicApi.getSettings()
    .then((res) => setSettings(res.data))
    .catch(console.error);
  }, []);

  if (!settings) return null;

  const mapsUrl = `https://maps.google.com/maps?q=${encodeURIComponent(settings.maps_query || settings.address)}&t=&z=16&ie=UTF8&iwloc=&output=embed`;

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#111]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div ref={headerRef} className={`reveal mb-16 ${headerVisible ? 'visible' : ''}`}>
          <p className="text-sm tracking-[0.3em] uppercase text-[#FFD700] mb-4 font-medium" style={{fontFamily:'Manrope'}}>{t('contact.label')}</p>
          <h2 data-testid="contact-heading" className="text-3xl md:text-4xl lg:text-5xl tracking-tight text-white">{t('contact.title')}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          <div ref={mapRef} data-testid="google-maps-container"
            className={`reveal-left overflow-hidden rounded-sm border border-[#222] aspect-[4/3] md:aspect-auto md:min-h-[400px] ${mapVisible ? 'visible' : ''}`}>
            <iframe title="Petit Hem Location" src={mapsUrl}
              className="w-full h-full maps-dark-filter" style={{ border: 0, minHeight: '400px' }}
              allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>

          <div ref={infoRef} className={`reveal-right flex flex-col justify-center gap-10 ${infoVisible ? 'visible' : ''}`}>
            <div className="flex gap-4" data-testid="contact-address">
              <MapPin className="text-[#FFD700] flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-sm tracking-[0.2em] uppercase text-[#FFD700] mb-2 font-medium" style={{fontFamily:'Manrope'}}>{t('contact.address')}</p>
                <p className="text-white text-lg leading-relaxed">{settings.address}</p>
              </div>
            </div>
            <div className="flex gap-4" data-testid="contact-phone">
              <Phone className="text-[#FFD700] flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-sm tracking-[0.2em] uppercase text-[#FFD700] mb-2 font-medium" style={{fontFamily:'Manrope'}}>{t('contact.phone')}</p>
                <a href={`tel:${settings.phone}`} className="text-white text-lg hover:text-[#FFD700] transition-colors">{settings.phone}</a>
              </div>
            </div>
            <div className="flex gap-4" data-testid="contact-hours">
              <Clock className="text-[#FFD700] flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-sm tracking-[0.2em] uppercase text-[#FFD700] mb-2 font-medium" style={{fontFamily:'Manrope'}}>{t('contact.hours')}</p>
                <div className="space-y-2">
                  {(settings.hours || []).map((h, i) => (
                    <div key={i} className="flex justify-between gap-8">
                      <span className="text-[#B0B0B0]">{lang === 'fr' ? h.days_fr : h.days_en}</span>
                      <span className="text-white font-medium whitespace-nowrap">{h.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
